import { Identification } from 'cpfr-common';
import { ValidatedSelect } from '@cp-shared-19/frontend-ui';
import { Layout, Paragraph } from '@vwfs-bronson/bronson-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormikProps, useFormikContext } from 'formik';
import {
    BalanceReasonSelection,
    EmailInput,
    FileUpload,
    PhonenumberInput,
    TextArea,
    TextInput,
} from '../../simple-form-components';
import { monthNames } from '../../../../../../../utils/monthNames';
import { RequestInitialValues } from '../../../../initialValues';
import { AmountInput } from './AmountInput';

export type AuthorizationDebitFormProps = {
    identification?: Identification;
};

export const AuthorizationDebitForm: React.FC<AuthorizationDebitFormProps> = ({ identification }) => {
    const { t } = useTranslation('request');
    const formikContext: FormikProps<RequestInitialValues> = useFormikContext();
    const translationPrefix = 'category-selection.authorization-debit';

    const translate = (key: string): string => {
        return t(`${translationPrefix}.${key}`);
    };

    const isNameSurnameDisabled = !!identification?.data.name && !!identification.data.surname;

    const fileUploadLabels: Record<string, string> = {
        'Ventes d’un bien immobilier':
            "Copie intégrale de l'acte de vente / Attestation datée et signée par le notaire indiquant l'acquéreur, le vendeur, la nature du bien, la nature et la valeur de la transaction réalisée (acte notarié et / ou chèque émis par le notaire)",
        Crédit: "Copie de l'offre de crédit",
        'Vente de parts de société':
            "Copie intégrale de l'acte de vente ou du protocole de cession / Attestation d'un notaire ou d'un avocat certifiant la vente, son prix et le montant revenant à l'adhérent / Copie des statuts",
        'Distribution de dividendes': "Attestation du comptable certifiant l'opération",
        Trésorerie:
            'Attestation datée et signée de la banque attestant du montant disponible en Trésorerie ou un relevé de compte bancaire',
        'Donation/héritage':
            "Copie de l'acte de donation ou de la déclaration du don de sommes d'argent ou acte notarié ou copie du courrier avec l'objet du règlement et du chèque émis par le notaire",
        Epargne:
            'Relevé de portefeuille ou de compte ou Attestation datée et signée de la banque attestant du montant disponible en Epargne',
        'Gain aux jeux':
            "Copie de la lettre chèque et attestation nominative de la Fédération ou de l'organisme de loterie ayant attribué le gain, précisant le montant reçu",
    };

    return (
        <>
            <Layout.Item>
                <p
                    dangerouslySetInnerHTML={{
                        __html: translate('description'),
                    }}
                />
            </Layout.Item>
            <Layout.Item>
                <EmailInput
                    label={translate('email')}
                    name={'authorizationDebit.email'}
                    inputId="email-authorization-debit-form"
                />
            </Layout.Item>
            <Layout.Item>
                <PhonenumberInput
                    label={translate('phonenumber')}
                    name={'authorizationDebit.phonenumber'}
                    inputId="phone-number-authorization-debit-form"
                />
            </Layout.Item>
            <Layout.Item>
                <TextInput
                    label={translate('name-surname')}
                    name={'authorizationDebit.nameSurname'}
                    disabled={isNameSurnameDisabled}
                />
            </Layout.Item>
            <Layout.Item>
                <TextInput label={translate('authorizes')} name={'authorizationDebit.authorizes'} disabled />
            </Layout.Item>
            <Layout.Item>
                <AmountInput label={translate('amount')} name={'authorizationDebit.amount'} />
            </Layout.Item>
            <Layout.Item>
                <ValidatedSelect
                    className="c-form-field--no-floating"
                    name={'authorizationDebit.expirationMonth'}
                    label={translate('expiration-date')}
                    selectItems={monthNames.map((month) => ({
                        value: month,
                        label: month,
                    }))}
                />
            </Layout.Item>
            <Layout.Item>
                <BalanceReasonSelection
                    label={translate('balance-reason')}
                    name={'authorizationDebit.balanceReason'}
                    selectId="balance-reason-authorization-debit-from"
                    isMandatory={true}
                    customerType={(identification as Identification)?.customerType}
                />
            </Layout.Item>
            <Layout.Item>
                <Paragraph>{translate('legal-desc')}</Paragraph>
            </Layout.Item>

            {formikContext.values.authorizationDebit.balanceReason && (
                <Layout.Item>
                    <FileUpload
                        name={'authorizationDebit.files'}
                        label={fileUploadLabels[formikContext.values.authorizationDebit.balanceReason] || ''}
                        isMandatory={true}
                        multiple
                    />
                </Layout.Item>
            )}

            <Layout.Item>
                <TextArea
                    label={translate('other-balance-reason')}
                    name={'authorizationDebit.otherBalanceReason'}
                    isMandatory={false}
                />
            </Layout.Item>
        </>
    );
};
